import React, { useEffect, useState } from "react";
import { Modal, Input, Spin, Alert } from "antd";
import { useDispatch } from "react-redux";
import { getGroupMembersCSV } from "../../../actions/api";
import { types as at, errorAction } from "@authzsvc/api-lib";
import TooltipIcon from "../../common/TooltipIcon";
import { showErrorNotification, showSuccessNotification } from "../../../util";
import {
  UploadOutlined,
  DownloadOutlined,
  FileOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import LabeledSwitch from "../../common/LabeledSwitch";

const CSVModal = ({ isModalOpen, onClose, group, getMembers, isDownload }) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [isOverwrite, setIsOverwrite] = useState(false);
  const [isCompatibilityMode, setIsCompatibilityMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [streamMessage, setStreamMessage] = useState("");
  const dispatch = useDispatch();

  // Accepts a callback to set local failure state
  function handleFailure(message, setLocalFailed) {
    showErrorNotification(
      "Import failed",
      message ||
        "An error occurred while importing the CSV. Please ensure the file is correctly formatted and try again."
    );
    setLoading(false);
    setFile(null);
    if (setLocalFailed) setLocalFailed(true);
    onClose();
  }

  const handleCancel = () => {
    if (loading) return; // Prevent closing while loading
    setVisible(false);
    setFile(null);
    setIsOverwrite(false);
    setIsCompatibilityMode(false);
    onClose();
  };

  const handleFileChange = (event) => {
    const newFile = event.target.files[0];
    if (newFile) {
      setFile(newFile);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const newFile = event.dataTransfer.files[0];
    if (newFile) {
      setFile(newFile);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // Accepts a callback to set local failure state
  const processStream = async (responseBody, setLocalFailed) => {
    const decoder = new TextDecoder();
    let buffer = "";

    for await (const chunk of responseBody) {
      buffer += decoder.decode(chunk, { stream: true });

      let newlineIndex;

      // Processes one line at a time, no extra arrays which is more memory efficient. buffer.split("\n"); creates a new array and it is a bit costly for large streams
      while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);

        if (!line) continue;

        try {
          const json = JSON.parse(line);
          if (json.Status && json.Status === "Failed") {
            handleFailure(json.Message, setLocalFailed);
            return;
          }
          if (json.Message) {
            setStreamMessage(json.Message);
          }

          if (json.done || json.finished) {
            setStreamMessage("Import completed.");
          }
        } catch {
          console.warn("Skipping invalid NDJSON line:", line);
        }
      }
    }
  };

  const handleImportCSV = async () => {
    if (!file) return;

    setLoading(true);
    setStreamMessage("Starting import...");

    const csvBlob = new Blob([file], { type: "text/csv" });
    const formData = new FormData();
    formData.append("file", csvBlob, file.name);
    const tokenName = "AuthService";

    // Use a local variable to track failure
    let localFailed = false;
    const setLocalFailed = (val) => {
      localFailed = val;
    };

    try {
      let token = sessionStorage.getItem(tokenName);

      const response = await fetch(
        `${process.env.REACT_APP_AUTH_SERVICE_URL}/Group/${group.groupIdentifier}/members/csv/stream?compatibilityMode=${isCompatibilityMode}&replace=${isOverwrite}`,
        {
          method: "PUT",
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        handleFailure(undefined, setLocalFailed);
        return;
      }

      await processStream(response.body, setLocalFailed);
      if (!localFailed) {
        await getMembers();
        showSuccessNotification("CSV imported successfully!");
        setFile(null);
        onClose();
      }
    } catch (err) {
      showErrorNotification("Streaming error:", err.message);
    }

    setLoading(false);
  };

  const handleDownloadCSV = async () => {
    const result = await dispatch(
      getGroupMembersCSV(group.groupIdentifier, {
        compatibilityMode: isCompatibilityMode,
      })
    );

    if (result.type === errorAction(at.GET_GROUP_MEMBERS_CSV)) {
      showErrorNotification(result.payload.title, result.payload.data.message);
    } else {
      const blob = new Blob([result.payload], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `group-${group.groupIdentifier}-members.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showSuccessNotification("CSV has been downloaded successfully!");

      onClose();
    }
  };

  useEffect(() => {
    setVisible(isModalOpen);
  }, [isModalOpen]);

  return (
    <Modal
      width={500}
      open={visible}
      centered
      onCancel={handleCancel}
      onOk={isDownload ? handleDownloadCSV : handleImportCSV}
      okText={isDownload ? "Download" : "Import"}
      okButtonProps={{
        icon: isDownload ? <DownloadOutlined /> : <UploadOutlined />,
        ...(!isDownload && !file ? { disabled: true } : {}),
        loading: loading,
      }}
      cancelButtonProps={{
        disabled: loading,
      }}
      closable={!loading}
      maskClosable={!loading}
      title={
        isDownload ? "Download a list of members" : "Add a list of members"
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem 1rem" }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            size="large"
          />
          <p style={{ marginTop: "1.5rem", fontSize: "16px", color: "#666" }}>
            Processing CSV file...
          </p>

          {streamMessage && (
            <pre
              style={{
                marginTop: "1rem",
                padding: "0.75rem",
                background: "#f7f7f7",
                borderRadius: 4,
                textAlign: "left",
                fontSize: 13,
                color: "#333",
                maxHeight: 200,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                border: "1px solid #ddd",
              }}
            >
              {streamMessage}
            </pre>
          )}
        </div>
      ) : (
        <div style={{ textAlign: "start" }}>
          <p style={{ marginBottom: "1rem" }}>
            {isDownload ? (
              <>
                Click 'Download' to retrieve the current list of members in CSV
                format.
              </>
            ) : (
              <>
                Drag and drop your CSV file here or click to select a file.
                <br />
                Ensure your CSV has the{" "}
                <a
                  href="https://auth.docs.cern.ch/groups/csv/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  required format
                </a>
                .
              </>
            )}
          </p>
          <LabeledSwitch
            checked={isCompatibilityMode}
            onChange={() => setIsCompatibilityMode(!isCompatibilityMode)}
            label="E-groups compatibility"
            labelContent={
              <TooltipIcon
                title={
                  isDownload
                    ? "Select this to download the current list of members compatible with E-groups. If you download a file compatible with E-groups, remember to toggle the 'E-groups compatibility' option when importing it back."
                    : "Select this if your CSV file uses the legacy Egroups format."
                }
              />
            }
            style={{
              marginBottom: "1rem",
            }}
          />
          {!isDownload && (
            <>
              <LabeledSwitch
                checked={isOverwrite}
                onChange={() => setIsOverwrite(!isOverwrite)}
                label="Overwrite existing members"
                labelContent={
                  <TooltipIcon title="Selecting this option will completely replace current members with those in the CSV. Consider downloading the current list for backup if unsure." />
                }
                style={{
                  marginBottom: "1rem",
                }}
              />
              {isOverwrite && (
                <Alert
                  message="This will replace all current members with those in the CSV. This action cannot be undone!"
                  type="warning"
                  showIcon
                  style={{ margin: "0.5rem 0 1rem 0" }}
                />
              )}
            </>
          )}

          {!isDownload && (
            <div
              id="csv-members-input-modal"
              className="custom-file-upload"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              style={{
                marginBottom: "1rem",
                justifyContent: "center",
                padding: "1rem",
                border: "2px dashed #ccc",
                borderRadius: "4px",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: "none" }}
                id="file-input"
              />
              <label
                htmlFor="file-input"
                style={{
                  cursor: "pointer",
                  border: "1px solid #ccc",
                  padding: "10px 20px",
                  borderRadius: "4px",
                  backgroundColor: "#f9f9f9",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxSizing: "border-box",
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <FileOutlined style={{ marginRight: 8 }} />
                {file ? file.name : "Drag & Drop or Choose File"}
              </label>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CSVModal;
